import { getDB } from './init';
import { STORE_NAMES, LocalPhoto } from './schema';

/**
 * Photo Store Operations
 */

export async function addPhoto(photo: LocalPhoto): Promise<void> {
  const db = await getDB();
  await db.put(STORE_NAMES.PHOTOS, photo);
}

export async function addPhotos(photos: LocalPhoto[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(STORE_NAMES.PHOTOS, 'readwrite');
  await Promise.all([
    ...photos.map(photo => tx.store.put(photo)),
    tx.done,
  ]);
}

export async function getPhoto(photoId: string): Promise<LocalPhoto | undefined> {
  const db = await getDB();
  return db.get(STORE_NAMES.PHOTOS, photoId);
}

export async function getPhotosByTransaction(transactionId: string): Promise<LocalPhoto[]> {
  const db = await getDB();
  const photos = await db.getAllFromIndex(STORE_NAMES.PHOTOS, 'transactionId', transactionId);
  // Sort by sequence number
  return photos.sort((a, b) => a.sequence - b.sequence);
}

export async function getUnuploadedPhotos(): Promise<LocalPhoto[]> {
  const db = await getDB();
  return db.getAllFromIndex(STORE_NAMES.PHOTOS, 'uploaded', 0 as IDBValidKey);
}

export async function updatePhoto(photoId: string, updates: Partial<LocalPhoto>): Promise<void> {
  const db = await getDB();
  const photo = await db.get(STORE_NAMES.PHOTOS, photoId);
  if (!photo) {
    throw new Error(`Photo ${photoId} not found`);
  }
  await db.put(STORE_NAMES.PHOTOS, { ...photo, ...updates });
}

export async function markPhotoUploaded(photoId: string): Promise<void> {
  await updatePhoto(photoId, { uploaded: true });
}

export async function deletePhoto(photoId: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAMES.PHOTOS, photoId);
}

export async function deletePhotosByTransaction(transactionId: string): Promise<void> {
  const db = await getDB();
  const photos = await getPhotosByTransaction(transactionId);
  const tx = db.transaction(STORE_NAMES.PHOTOS, 'readwrite');
  await Promise.all([
    ...photos.map(photo => tx.store.delete(photo.photoId)),
    tx.done,
  ]);
}

export async function clearPhotos(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAMES.PHOTOS);
}

/**
 * Get total storage size used by photos (approximate)
 */
export async function getPhotosStorageSize(): Promise<number> {
  const db = await getDB();
  const photos = await db.getAll(STORE_NAMES.PHOTOS);
  return photos.reduce((total, photo) => total + photo.imageBlob.size, 0);
}

/**
 * Photo Arrangement Persistence
 */

/**
 * Save photo arrangement by updating slot assignments for photos in a transaction
 * @param transactionId - The transaction ID
 * @param photoArrangement - Map of slotIndex to photoId
 * @throws Error if any photo is not found or update fails
 */
export async function savePhotoArrangement(
  transactionId: string,
  photoArrangement: Map<number, string>
): Promise<void> {
  const db = await getDB();
  
  try {
    // Get all photos for this transaction first (outside of write transaction)
    const photos = await getPhotosByTransaction(transactionId);
    
    // Create a map of photoId to photo for quick lookup
    const photoMap = new Map(photos.map(p => [p.photoId, p]));
    
    // Validate that all photos in the arrangement exist
    for (const [, photoId] of photoArrangement.entries()) {
      if (!photoMap.has(photoId)) {
        throw new Error(`Photo ${photoId} not found in transaction ${transactionId}`);
      }
    }
    
    // Now perform the updates in a single transaction
    const tx = db.transaction(STORE_NAMES.PHOTOS, 'readwrite');
    const updates: Promise<IDBValidKey>[] = [];
    
    // First, clear all slot assignments for photos in this transaction
    for (const photo of photos) {
      const updatedPhoto = { ...photo, slotIndex: undefined };
      updates.push(tx.store.put(updatedPhoto));
    }
    
    // Then, set slot assignments for photos in the arrangement
    for (const [slotIndex, photoId] of photoArrangement.entries()) {
      const photo = photoMap.get(photoId)!;
      const updatedPhoto = { ...photo, slotIndex };
      updates.push(tx.store.put(updatedPhoto));
    }
    
    await Promise.all([...updates, tx.done]);
  } catch (error) {
    throw new Error(`Failed to save photo arrangement: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Load photo arrangement from IndexedDB for a transaction
 * @param transactionId - The transaction ID
 * @returns Map of slotIndex to photoId for photos with slot assignments
 */
export async function loadPhotoArrangement(transactionId: string): Promise<Map<number, string>> {
  try {
    const photos = await getPhotosByTransaction(transactionId);
    const arrangement = new Map<number, string>();
    
    for (const photo of photos) {
      if (photo.slotIndex !== undefined && photo.slotIndex !== null) {
        arrangement.set(photo.slotIndex, photo.photoId);
      }
    }
    
    return arrangement;
  } catch (error) {
    throw new Error(`Failed to load photo arrangement: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
